<template>
  <v-dialog v-model="dialog" persistent width="700">
    <template v-slot:activator="{ on }">
      <v-tooltip v-if="Boolean(assignment)" bottom>
        <template v-slot:activator="{ on }">
          <v-icon v-on="on" @click="openDialog" small color="primary"
            >mdi-pencil</v-icon
          >
        </template>
        <span>Edit Assignment</span>
      </v-tooltip>
      <v-btn
        v-else
        v-on="on"
        @click="openDialog"
        rounded
        outlined
        small
        color="primary"
      >
        <v-icon small color="primary">mdi-plus</v-icon>Assign Provider</v-btn
      >
    </template>
    <v-card>
      <validation-observer ref="form" v-slot="{ invalid }">
        <v-card-title color="primary">
          Assignment <v-spacer></v-spacer>
          <v-btn color="primary" icon @click="closeDialog()">
            <v-icon>mdi-close</v-icon>
          </v-btn></v-card-title
        >
        <v-card-text
          ><v-row>
            <v-col cols="12">
              <validation-provider rules="required" v-slot="{ errors }">
                <v-autocomplete
                  v-model="form.providerId"
                  label="Provider"
                  :items="providerOptions"
                  :item-text="(item) => getProviderOptionText(item)"
                  item-value="providerId"
                  item-disabled="disabled"
                  :error-messages="errors"
                  :clearable="canEdit"
                  :readonly="!canEdit"
                ></v-autocomplete></validation-provider></v-col
          ></v-row>
          <v-row>
            <v-col cols="12" sm="6">
              <date-picker
                :field="{ label: 'Approval Start Date' }"
                :value="form.approvalStartDate"
                :validations="{
                  minDate: servicePlan.startDate,
                  maxDate: servicePlan.endDate,
                }"
                @fieldChange="form.approvalStartDate = $event"
                :readonly="!canEdit"
              >
              </date-picker></v-col
            ><v-col cols="12" sm="6">
              <date-picker
                :field="{ label: 'Approval End Date' }"
                :value="form.approvalEndDate"
                :validations="{
                  minDate: form.approvalStartDate,
                  maxDate: maxApprovalEndDate,
                }"
                @fieldChange="form.approvalEndDate = $event"
                :readonly="!canEdit"
              >
              </date-picker></v-col></v-row
          ><v-row
            ><v-col cols="12" sm="6">
              <date-picker
                :field="{ label: 'Start Date' }"
                :value="form.startDate"
                readonly
              >
              </date-picker></v-col
            ><v-col cols="12" sm="6">
              <date-picker
                :field="{ label: 'End Date' }"
                :value="form.endDate"
                :validations="{
                  minDate: form.startDate,
                  maxDate: form.approvalEndDate,
                }"
                @fieldChange="form.endDate = $event"
                :readonly="!canEdit"
              >
              </date-picker></v-col
          ></v-row>
          <v-row>
            <v-col cols="12" sm="3">
              <v-autocomplete
                v-model="form.providerSpecLocationId"
                label="Location"
                :items="locations"
                :item-text="(item) => `${item.legacyLocationId} - ${item.name}`"
                item-value="sessionLocationId"
                :clearable="canEdit"
                :readonly="!canEdit"
              ></v-autocomplete
            ></v-col>
            <v-col cols="12" sm="3">
              <v-select
                v-model="form.providerSpecFrequency"
                :items="frequencies"
                label="Frequency"
                :clearable="canEdit"
                :readonly="!canEdit"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="3">
              <v-select
                v-model="form.providerSpecDurationMinutes"
                label="Duration"
                :items="durations"
                :clearable="canEdit"
                :readonly="!canEdit"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="3"
              ><v-text-field
                v-model.number="form.providerSpecGroupSize"
                label="Group Size"
                type="number"
                min="1"
                max="10"
                :readonly="!canEdit"
              >
              </v-text-field>
            </v-col> </v-row
        ></v-card-text>
        <v-card-actions v-if="canEdit">
          <v-spacer></v-spacer>
          <v-btn small color="primary" text @click="closeDialog()"
            >Cancel</v-btn
          >
          <v-btn
            small
            color="primary"
            :disabled="invalid || loading"
            @click="save"
            >Save</v-btn
          >
        </v-card-actions>
      </validation-observer>
    </v-card>
  </v-dialog>
</template>

<script>
import Assignment from "../../../objs/Assignment";
export default {
  props: ["servicePlanId", "assignment"],
  data() {
    return {
      dialog: false,
      loading: false,
      form: new Assignment(),
      frequencies: [
        1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
      ],
      durations: [30, 45, 60, 90, 360],
    };
  },
  computed: {
    canEdit() {
      return (
        this.$store.getters["securityModule/isEIAdmin"] ||
        this.$store.getters["securityModule/isSEITAdmin"]
      );
    },
    locations() {
      return (
        this.$store.state.optionsModule.sessionLocations
          .slice()
          .sort((a, b) =>
            a.legacyLocationId.localeCompare(b.legacyLocationId)
          ) || []
      );
    },
    providerOptions() {
      var acceptedDisciplines = this.$store.getters[
        "optionsModule/getDisciplinesForService"
      ](this.servicePlan.serviceId);
      var acceptedDisciplineIds = new Set(
        acceptedDisciplines.map((d) => d.disciplineId)
      );
      return (
        // disable providers that are blocked or don't have an accepted discipline for this service
        (
          this.$store.state.optionsModule.providers.map((x) => {
            return {
              ...x,
              disabled:
                x.isBlocked ||
                !x.disciplines.some((disc) =>
                  acceptedDisciplineIds.has(disc.disciplineId)
                ),
            };
          }) || []
        )
          .slice()
          .sort((a, b) => a.lastName.localeCompare(b.lastName))
      );
    },
    servicePlan() {
      var plan = this.$store.state.plansModule.plans.find((item) =>
        item.servicePlans.some(
          (innerItem) => innerItem.servicePlanId == this.servicePlanId
        )
      );
      return plan.servicePlans.find(
        (item) => item.servicePlanId == this.servicePlanId
      );
    },
    currentSemester() {
      return this.$store.getters["optionsModule/getCurrentSemester"];
    },
    semester() {
      return this.$store.getters["optionsModule/getSemesterByDate"](
        this.form.approvalStartDate
      );
    },
    seitSemesterEndDate() {
      if (this.semester != null)
        return this.semester.seitEndDate != null
          ? new Date(this.semester.seitEndDate)
          : new Date(this.semester.semesterEndDate);
      return null;
    },
    defaultApprovalStartDate() {
      // service plan start date or (seit) semester start date - whichever comes later
      var semesterStartDate =
        this.currentSemester.seitStartDate == null
          ? this.currentSemester.semesterStartDate
          : this.currentSemester.seitStartDate;
      return semesterStartDate == null ||
        new Date(this.servicePlan.startDate) > new Date(semesterStartDate)
        ? new Date(this.servicePlan.startDate)
        : new Date(semesterStartDate);
    },
    defaultApprovalEndDate() {
      // service plan end date or (seit) semester end date - whichever comes first
      return this.seitSemesterEndDate == null ||
        new Date(this.servicePlan.endDate) < new Date(this.seitSemesterEndDate)
        ? new Date(this.servicePlan.endDate)
        : new Date(this.seitSemesterEndDate);
    },
    maxApprovalEndDate() {
      // service plan end date or semester end date - whichever comes first
      return this.semester == null ||
        new Date(this.servicePlan.endDate) <
          new Date(this.semester.semesterEndDate)
        ? new Date(this.servicePlan.endDate)
        : new Date(this.semester.semesterEndDate);
    },
  },
  methods: {
    getProviderOptionText(item) {
      var disciplines = item.disciplines.map((x) => x.code).join(", ");
      return `${item.firstName} ${item.lastName} (${item.legacyProviderId}) - ${disciplines}`;
    },
    closeDialog() {
      this.dialog = false;
      this.form = new Assignment();
    },
    openDialog() {
      if (this.assignment) {
        // edit assignment
        this.form = { ...this.assignment };
        this.form.approvalStartDate = this.assignment.approvalStartDate
          ? new Date(this.assignment.approvalStartDate)
          : null;
        this.form.approvalEndDate = this.assignment.approvalEndDate
          ? new Date(this.assignment.approvalEndDate)
          : null;
        this.form.startDate = this.assignment.startDate
          ? new Date(this.assignment.startDate)
          : null;
        this.form.endDate = this.assignment.endDate
          ? new Date(this.form.endDate)
          : null;
      } else {
        // new assignment
        // default approval start date to service plan start date or semester start date - whichever comes later
        this.form.approvalStartDate = this.defaultApprovalStartDate;
        // default approval end date to service plan end date or semester end date - whichever comes first
        this.form.approvalEndDate = this.defaultApprovalEndDate;
        // default provider specific info to service plan info
        this.form.providerSpecLocationId = this.servicePlan.sessionLocationId;
        this.form.providerSpecFrequency = this.servicePlan.frequency;
        this.form.providerSpecDurationMinutes =
          this.servicePlan.durationMinutes;
        this.form.providerSpecGroupSize = this.servicePlan.groupSize;
      }
      this.form.servicePlanId = this.servicePlanId;
      this.dialog = true;
    },
    async save() {
      this.loading = true;
      let message;
      // update
      if (this.assignment) {
        message = await this.$store.dispatch(
          "plansModule/updateProviderAssignment",
          this.form
        );
      } else {
        // create
        message = await this.$store.dispatch(
          "plansModule/createProviderAssignment",
          this.form
        );
      }
      if (message) {
        this.$store.commit("uxModule/setSnackbarMsg", message);
      } else {
        this.$store.commit(
          "uxModule/setSnackbarMsg",
          "Assignment saved successfully"
        );
      }
      this.loading = false;
      this.$store.commit("uxModule/setShowSnackbar", true);
      this.closeDialog();
    },
  },
};
</script>
